/**
 * Turns a base id + checked overlay ids into actual URLs.
 *
 * Shared by every `SharedVolumeViewer` caller: the NiiVue pane and the slice
 * canvas both need the resolved URLs (NiiVue to load the volume, the canvas to
 * fetch and decode it), so this is computed once per selection change rather
 * than inside the viewer itself.
 */

import * as React from "react";

import { overlayColor } from "./BaseOverlayPicker";
import type { SliceOverlaySpec } from "./SliceCanvas";
import type { SharedVolumeFile } from "./SharedVolumeViewer";

export function useResolvedVolumeUrls(
  files: SharedVolumeFile[],
  baseId: string | null,
  overlayIds: string[],
): { baseUrl: string | null; overlays: SliceOverlaySpec[] } {
  const byId = React.useMemo(() => new Map(files.map((f) => [f.id, f])), [files]);
  const [baseUrl, setBaseUrl] = React.useState<string | null>(null);
  const [overlays, setOverlays] = React.useState<SliceOverlaySpec[]>([]);

  React.useEffect(() => {
    let live = true;
    const file = baseId ? byId.get(baseId) : null;
    if (!file) {
      setBaseUrl(null);
      return;
    }
    void file.resolve().then((url) => {
      if (live) setBaseUrl(url);
    });
    return () => {
      live = false;
    };
  }, [baseId, byId]);

  React.useEffect(() => {
    let live = true;
    void Promise.all(
      overlayIds.map(async (id, i) => {
        const file = byId.get(id);
        const url = (await file?.resolve()) ?? null;
        return url ? { url, color: overlayColor(i), label: file?.label ?? id } : null;
      }),
    ).then((specs) => {
      if (live) setOverlays(specs.filter((s): s is SliceOverlaySpec => s != null));
    });
    return () => {
      live = false;
    };
  }, [overlayIds, byId]);

  return { baseUrl, overlays };
}
